import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Check, X } from 'lucide-react';
import GoldRule from '../components/GoldRule';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get('session_id');
  const [status, setStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sid) return;
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sid}`);
        if (stop) return;
        setStatus(data);
        if (data.payment_status !== 'paid' && attempts < 20) {
          setAttempts((a) => a + 1);
          setTimeout(poll, 1500);
        }
      } catch {
        setStatus({ payment_status: 'error' });
      }
    };
    poll();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid]);

  const paid = status?.payment_status === 'paid';

  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center" data-testid="payment-success-page">
      <div className="eyebrow text-gold mb-6">{paid ? 'Payment received' : 'Confirming…'}</div>
      <h1 className="font-display italic text-5xl md:text-6xl text-mb leading-[1]">
        {paid ? 'The atelier has your order.' : 'Verifying with Stripe…'}
      </h1>
      <div className="mt-10 mx-auto"><GoldRule size="med" /></div>
      {paid ? (
        <>
          <p className="mt-10 font-display italic text-[18px] text-mb-muted">
            {status?.display_name}
          </p>
          <p className="mt-3 eyebrow text-mb">
            {status?.currency?.toUpperCase()} {Number(status?.amount || 0).toFixed(2)}
          </p>
          <p className="mt-10 text-[14px] text-mb-muted max-w-md mx-auto">
            An atelier representative will write within one business day with next steps.
          </p>
        </>
      ) : (
        <div className="mt-10 inline-flex items-center gap-3 text-mb-muted">
          <Loader2 size={16} className="animate-spin" />
          <span className="eyebrow-sm">This can take a few seconds.</span>
        </div>
      )}
      <div className="mt-14">
        <Link to="/" className="btn-outline-gold" data-testid="payment-back-home">Return home</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-[900px] mx-auto px-6 md:px-12 py-24 md:py-32 text-center" data-testid="payment-cancel-page">
      <div className="eyebrow text-mb-muted mb-6">Cancelled</div>
      <h1 className="font-display italic text-5xl md:text-6xl text-mb leading-[1]">
        Nothing was charged.
      </h1>
      <div className="mt-10 mx-auto"><GoldRule size="med" /></div>
      <p className="mt-10 font-display italic text-[18px] text-mb-muted">
        Take your time. The atelier will be here.
      </p>
      <div className="mt-14 flex justify-center gap-4">
        <Link to="/sets" className="btn-outline-gold">View the sets</Link>
        <Link to="/contact" className="btn-gold">Send an enquiry</Link>
      </div>
    </div>
  );
}
